import React from 'react';
import { Checkbox, Space, Typography, Tooltip } from 'antd';
import { CheckCircleOutlined, LockOutlined, ArrowRightOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface FocusAreaStatus {
  properties: boolean;
  states: boolean;
  transitions: boolean;
  processes: boolean;
}

interface FocusAreaSelectorProps {
  selectedAreas: Set<string>;
  onSelectionChange: (areas: Set<string>) => void;
  approvalStatus: FocusAreaStatus;
}

const FOCUS_AREAS = [
  { key: 'properties', label: 'Properties' },
  { key: 'states', label: 'States' },
  { key: 'transitions', label: 'Transitions' },
  { key: 'processes', label: 'Processes' },
] as const;

const FOCUS_AREA_SEQUENCE = ['properties', 'states', 'transitions', 'processes'] as const;

export const FocusAreaSelector: React.FC<FocusAreaSelectorProps> = ({
  selectedAreas,
  onSelectionChange,
  approvalStatus,
}) => {
  // Check if previous steps are approved
  const arePreviousStepsApproved = (areaKey: string): boolean => {
    const index = FOCUS_AREA_SEQUENCE.indexOf(areaKey as any);
    if (index === -1 || index === 0) return true; // First step or invalid
    
    // Check all previous steps
    for (let i = 0; i < index; i++) {
      const prevKey = FOCUS_AREA_SEQUENCE[i];
      if (!approvalStatus[prevKey as keyof FocusAreaStatus]) {
        return false;
      }
    }
    return true;
  };

  // Check if this is the next unapproved step
  const isNextUnapprovedStep = (areaKey: string): boolean => {
    // Must not be approved
    if (approvalStatus[areaKey as keyof FocusAreaStatus]) {
      return false;
    }
    
    // Previous steps must be approved
    if (!arePreviousStepsApproved(areaKey)) {
      return false;
    }
    
    // Check if this is the first unapproved step
    const index = FOCUS_AREA_SEQUENCE.indexOf(areaKey as any);
    for (let i = 0; i < index; i++) {
      const prevKey = FOCUS_AREA_SEQUENCE[i];
      if (!approvalStatus[prevKey as keyof FocusAreaStatus]) {
        return false; // There's an earlier unapproved step
      }
    }
    return true;
  };

  // Determine checkbox state for an area
  const getCheckboxState = (areaKey: string): {
    checked: boolean;
    disabled: boolean;
    statusText: string;
    icon: React.ReactNode;
  } => {
    const isApproved = approvalStatus[areaKey as keyof FocusAreaStatus];
    const isNext = isNextUnapprovedStep(areaKey);
    const previousApproved = arePreviousStepsApproved(areaKey);

    if (isApproved) {
      return {
        checked: true,
        disabled: true,
        statusText: '✓ Approved',
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      };
    } else if (isNext && previousApproved) {
      // Auto-check next step if nothing is selected, or if this area is selected
      const shouldBeChecked = selectedAreas.size === 0 || selectedAreas.has(areaKey);
      return {
        checked: shouldBeChecked,
        disabled: false,
        statusText: '→ Next Step',
        icon: <ArrowRightOutlined style={{ color: '#1890ff' }} />,
      };
    } else {
      return {
        checked: false,
        disabled: true,
        statusText: '🔒 Locked',
        icon: <LockOutlined style={{ color: '#d9d9d9' }} />,
      };
    }
  };

  const handleCheckboxChange = (areaKey: string, checked: boolean) => {
    const newSelection = new Set(selectedAreas);
    if (checked) {
      newSelection.add(areaKey);
    } else {
      newSelection.delete(areaKey);
    }
    onSelectionChange(newSelection);
  };

  return (
    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
      <Text strong style={{ display: 'block', marginBottom: '8px' }}>
        Focus Areas:
      </Text>
      <Space wrap>
        {FOCUS_AREAS.map(({ key, label }) => {
          const state = getCheckboxState(key);
          return (
            <Tooltip
              key={key}
              title={
                state.disabled
                  ? state.checked
                    ? 'This step has been approved and cannot be changed'
                    : 'Complete previous steps before working on this area'
                  : 'Select to focus on this area in your message'
              }
            >
              <Checkbox
                checked={state.checked}
                disabled={state.disabled}
                onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                style={{
                  opacity: state.disabled && state.checked ? 0.6 : 1,
                }}
              >
                <Space size={4}>
                  {state.icon}
                  <Text
                    style={{
                      color: state.disabled && !state.checked ? '#bfbfbf' : undefined,
                    }}
                  >
                    {label}
                  </Text>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: '11px',
                      fontStyle: 'italic',
                      color: state.disabled && !state.checked ? '#bfbfbf' : undefined,
                    }}
                  >
                    ({state.statusText})
                  </Text>
                </Space>
              </Checkbox>
            </Tooltip>
          );
        })}
      </Space>
    </div>
  );
};

